import { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useClientTiers,
  useCurrentUserProfile,
  useUnreadAchievements,
} from '@busy/api';
import { CelebrationModal } from '../components/CelebrationModal';
import { greetingPhase } from '@busy/core';
import { useT } from '../lib/i18n';
import { WeatherTile } from '../components/today/WeatherTile';
import { StreakRibbon } from '../components/today/StreakRibbon';
import { DailyLessonCard } from '../components/today/DailyLessonCard';
import { NextMilestonesTile } from '../components/today/NextMilestonesTile';
import { CoachingInsightsTile } from '../components/today/CoachingInsightsTile';
import { ProfileCompleteBanner } from '../components/today/ProfileCompleteBanner';
import { WeeklyTargetReminder } from '../components/today/WeeklyTargetReminder';
import { AtRiskSummaryTile } from '../components/today/AtRiskSummaryTile';

// Today screen — the daily-pulse surface, ordered top → bottom:
//   Header (greeting + weather)
//   ProfileCompleteBanner (conditional)
//   At-risk client alert (daily nudge → /clients)
//   WeeklyTargetReminder
//   StreakRibbon
//   DailyLessonCard
//   Goals
//   NextMilestonesTile  ← moved below Goals so the daily-action items
//                          (lesson, goals) lead and the gamification
//                          progress lives at the bottom as a "what's
//                          next" footer
//
// The today's-wrap-up chip moved off this screen entirely — it now
// lives on the Dashboard above Past Recaps so the analytics surface
// owns retrospective summaries.

export function Today() {
  const navigate = useNavigate();
  const profile = useCurrentUserProfile();
  const t = useT();
  const unread = useUnreadAchievements();
  // At-risk count drives the Today-page nudge. The full at-risk list
  // lives on the Clients tab; this banner is the daily alert that taps
  // through to it. Hidden until tiers data is loaded so we don't flash
  // a stale "0" count on first paint.
  //
  // We also derive the sorted set of at-risk client IDs — the tile
  // hashes those IDs to track whether the user has marked the current
  // batch reviewed. As soon as the set changes (new at-risk client,
  // someone falls off, anything) the hash flips and the tile shows
  // again.
  const tiers = useClientTiers();
  const atRiskInfo = useMemo(() => {
    const ids: string[] = [];
    for (const row of tiers.data ?? []) {
      if (row.tier === 'at-risk') ids.push(row.client_id);
    }
    ids.sort();
    return { count: ids.length, ids };
  }, [tiers.data]);
  const [dismissedCelebration, setDismissedCelebration] = useState(false);
  const showCelebration =
    !dismissedCelebration && (unread.data ?? []).length > 0;

  const phase = greetingPhase();
  const phaseStr = t(`today.${phase}`);
  const firstName = profile.data?.first_name ?? null;
  const greeting = firstName ? `${phaseStr}, ${firstName}` : phaseStr;
  // Coach pep talk that matches the phase — morning huddle, halftime
  // check, finish strong. Static fallback if a translation is missing.
  const tagline = t(`today.tagline.${phase}`) || t('today.tagline');
  const lessonCardRef = useRef<HTMLDivElement>(null);

  function scrollToLesson() {
    lessonCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  return (
    <div className="max-w-page mx-auto p-4 md:p-6 space-y-5 pb-24">
      <header className="space-y-3">
        <h1 className="font-display text-4xl md:text-[42px] text-navy-900 dark:text-gold-500 leading-[1.05]">
          {greeting}
        </h1>
        <p className="text-ink-500 text-[15px]">{tagline}</p>
        <WeatherTile zip={profile.data?.zip ?? null} />
      </header>

      {/* Connect-your-booking-platform banner — hidden until v1.1 ships
          booking integrations. Lives in git history at this comment line;
          unhide by restoring the `!integrated && ( … )` JSX block, the
          `isIntegratedMode` import, and the `integrated` derived var. The
          related i18n keys (today.connectBanner.*) are still in the
          dictionary so the unhide is one paste. */}

      <ProfileCompleteBanner />

      {/* At-risk client alert — daily nudge to text the most overdue
          clients today. Only renders once tiers data has loaded so we
          don't flash a "0 at-risk" all-clear on first paint. Tapping
          it routes to /clients where the full collapsible list lives. */}
      {tiers.data && (
        <AtRiskSummaryTile count={atRiskInfo.count} clientIds={atRiskInfo.ids} />
      )}

      <WeeklyTargetReminder />

      <StreakRibbon onClick={scrollToLesson} />

      <div ref={lessonCardRef} className="scroll-mt-20">
        <DailyLessonCard />
      </div>

      {/* Up next section hidden until v1.1 ships booking-platform integration —
          without that, the only way to populate appointments is manual entry,
          which clashes with Busy's "open the app and start cutting" flow. To
          restore: lift the JSX back above and re-enable the appts query +
          tierByClient/clientById derived maps. The today.upNext / today.appt_*
          / today.noAppts* / today.apptsError i18n keys are still in the
          dictionary so the unhide is one paste. */}

      <section>
        <h2 className="font-display text-xl mb-3">{t('today.goals')}</h2>
        <div className="flex gap-2 flex-wrap">
          {(profile.data?.goals ?? []).map((goal) => (
            <button
              key={goal}
              type="button"
              onClick={() => navigate('/settings?section=lessonprefs')}
              aria-label={t('today.goals.editAria', { goal: t(goalKey(goal)) })}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-bg-alt border border-line text-ink-700 text-xs font-bold hover:border-navy-900/40 dark:hover:border-gold-500/40 hover:bg-surface transition-colors"
            >
              <TargetIcon className="w-3 h-3 text-gold-500" />
              {t(goalKey(goal))}
            </button>
          ))}
          {(profile.data?.goals ?? []).length === 0 && (
            <button
              type="button"
              onClick={() => navigate('/settings?section=lessonprefs')}
              className="text-sm text-ink-500 underline-offset-4 hover:underline"
            >
              {t('today.goals.empty')}
            </button>
          )}
        </div>
      </section>

      {/* Coaching Insights — cross-signal pattern detector. Surfaces
          the single highest-priority pattern matching this barber's
          current numbers (or nothing, if no pattern matches). Sits
          ABOVE NextMilestonesTile because patterns flagged here are
          higher-leverage than milestone progress. */}
      <CoachingInsightsTile />

      <NextMilestonesTile />

      {showCelebration && unread.data && (
        <CelebrationModal
          achievements={unread.data}
          onClose={() => setDismissedCelebration(true)}
        />
      )}
    </div>
  );
}

// Map goal IDs (matches Settings GOAL_OPTIONS) → i18n keys.
function goalKey(id: string): string {
  switch (id) {
    case 'retention':
      return 'goal.retention';
    case 'pricing':
      return 'goal.pricing';
    case 'time-management':
      return 'goal.timeManagement';
    case 'skill':
      return 'goal.skill';
    case 'marketing':
      return 'goal.marketing';
    case 'customer-service':
      return 'goal.customerService';
    default:
      return id;
  }
}

function TargetIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="12" cy="12" r="10" />
      <circle cx="12" cy="12" r="6" />
      <circle cx="12" cy="12" r="2" />
    </svg>
  );
}
